import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  Flag,
  Gift,
  Lock,
  Minus,
  Package,
  PackageCheck,
  Plus,
  RefreshCcw,
  ShieldCheck,
  ShoppingBag,
  Star,
  Timer,
  Truck,
  Trash2,
  X,
  ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import { useCountry } from "@/context/CountryContext";
import { useCart } from "@/context/CartContext";
import { usePlatform } from "@/context/PlatformContext";
import { COLORS, FREE_SHIPPING_THRESHOLD, PACKS, isColorAvailable, packImage, packCompareAt } from "@/data/products";
import { NextTierNudge } from "@/components/VolumeRewards";
import PaymentLogos from "@/components/PaymentLogos";
import RecentBuysTicker from "@/components/RecentBuysTicker";
import ExitIntentSave from "@/components/ExitIntentSave";
import CheckoutRedirectOverlay from "@/components/CheckoutRedirectOverlay";
import { openUrlInSystemBrowser, detectInAppBrowser } from "@/lib/inAppBrowser";
import { deliveryEstimate } from "@/lib/delivery";
import { stockLeft } from "@/lib/stock";
import { setAdvancedMatching, getAttribution } from "@/lib/metaPixel";

/** Tasteful AOV nudge: shows tracked-asset count and a 4-pack upgrade for small single-line carts. */
const Upsell = () => {
  const { items, addItem, removeItem } = useCart();
  const { platform } = usePlatform();
  const tracked = items.reduce((s, i) => s + i.packId * i.qty, 0);
  const small = items.length === 1 && items[0].qty === 1 && items[0].packId < 4 ? items[0] : null;
  const four = PACKS.find((p) => p.id === 4);

  const upgrade = () => {
    const color = COLORS.find((c) => c.name === small.color && isColorAvailable(c, 4)) || COLORS[0];
    removeItem(small.key);
    addItem({
      key: `4-${color.id}`,
      packId: 4,
      name: `${four.label} · ${four.name}`,
      color: color.name,
      colorHex: color.hex,
      image: packImage(color.id, 4, platform),
      price: four.price,
      qty: 1,
    });
  };

  return (
    <div data-testid="cart-upsell" className="mb-4 rounded-2xl bg-tracksy-cream p-4">
      <p className="text-sm font-bold text-tracksy-ink">You're tracking {tracked} item{tracked === 1 ? "" : "s"}.</p>
      <p className="mt-1 text-xs leading-relaxed text-tracksy-sub">Add another Tracksy to cover another vehicle, trailer or valuable asset.</p>
      {small && (
        <button
          type="button"
          data-testid="cart-upgrade-4pack"
          onClick={upgrade}
          className="mt-3 inline-flex items-center gap-2 rounded-full bg-tracksy-ink px-4 py-2 text-xs font-bold text-white transition-colors hover:bg-tracksy-orange"
        >
          Upgrade to the 4-Pack — ${four.price}
        </button>
      )}
    </div>
  );
};

const CartDrawer = () => {
  const {
    items, subtotal, discount, total, tier, trackerCount, isOpen, setIsOpen, updateQty, removeItem,
    travelCards, travelCardUnit, freeTravelCards, updateTravelCards, removeTravelCards,
  } = useCart();
  const remaining = Math.max(0, FREE_SHIPPING_THRESHOLD - subtotal);
  const progress = Math.min(100, (subtotal / FREE_SHIPPING_THRESHOLD) * 100);

  const [checkingOut, setCheckingOut] = useState(false);
  const [promo, setPromo] = useState(null); // "SAVE10" when exit-intent save is applied
  const [exitOpen, setExitOpen] = useState(false);
  const [email, setEmail] = useState(() => {
    try { return localStorage.getItem("tracksy-email") || ""; } catch { return ""; }
  });
  const [emailSaved, setEmailSaved] = useState(() => {
    try { return !!localStorage.getItem("tracksy-email"); } catch { return false; }
  });
  const { platform } = usePlatform();
  const { market, currency, isUS, setCode } = useCountry();
  const symbol = market?.symbol || "$";

  // Delivery estimate, re-ticks every minute so the countdown stays live.
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    if (!isOpen) return;
    const t = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(t);
  }, [isOpen]);
  // Pixel Advanced Matching: if we already have the shopper's email on this device
  // (prior checkout or saved cart), re-init on drawer open so InitiateCheckout
  // carries hashed `em` without any new UI.
  useEffect(() => {
    if (isOpen && email) setAdvancedMatching({ email });
  }, [isOpen, email]);

  // Microsoft Clarity funnel event — fires every time the drawer opens with items.
  // Clarity.set() tags the session with the dominant pack so you can filter
  // recordings by "shoppers who had 8-pack in cart but didn't buy".
  useEffect(() => {
    if (!isOpen || !window.clarity || items.length === 0) return;
    try {
      window.clarity("event", "cart_opened");
      const topPack = items.reduce((a, b) => (a.qty >= b.qty ? a : b));
      window.clarity("set", "cart_top_pack", `${topPack.packId}-pack`);
      window.clarity("set", "cart_total", String(Math.round(subtotal || 0)));
    } catch (e) { /* clarity may not be loaded on preview */ }
  }, [isOpen]);
  const delivery = useMemo(() => deliveryEstimate(now), [now]);

  // Reservation countdown — 10-minute hold that resets whenever the cart changes
  // (add, remove, qty bump). Pure urgency copy, not an actual Shopify hold; the
  // Shopify draft is only created at checkout. Shows as `9:48` next to the title.
  const RESERVE_MS = 10 * 60 * 1000;
  const cartSig = useMemo(() => items.map((i) => `${i.key}:${i.qty}`).join("|"), [items]);
  const [reserveStart, setReserveStart] = useState(() => Date.now());
  useEffect(() => { setReserveStart(Date.now()); }, [cartSig]);
  useEffect(() => {
    if (!isOpen || items.length === 0) return;
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, [isOpen, items.length]);
  const reservedLabel = useMemo(() => {
    if (items.length === 0) return "";
    const left = Math.max(0, RESERVE_MS - (now.getTime() - reserveStart));
    const mm = Math.floor(left / 60000);
    const ss = Math.floor((left % 60000) / 1000);
    if (left <= 0) return "Hurry — low stock";
    return `Held · ${mm}:${String(ss).padStart(2, "0")}`;
  }, [now, reserveStart, items.length]);

  // Promo deals summary — pack-level freeBonus (4-pack +1, 8-pack +2) and
  // compare-at pricing. These stack on top of the volume tier (10+/25+/50+/100+).
  // Backend's shopify draft creation already injects the matching 100%-discount
  // line items, so we only need to render the UX here.
  const packBonusTrackers = useMemo(
    () => items.reduce((s, i) => {
      const pack = PACKS.find((p) => p.id === i.packId);
      return s + (pack?.freeBonus || 0) * i.qty;
    }, 0),
    [items]
  );
  const compareAtSubtotal = useMemo(
    () => items.reduce((s, i) => {
      const pack = PACKS.find((p) => p.id === i.packId);
      const cmp = packCompareAt(pack, market);
      const unit = cmp && cmp > i.price ? cmp : i.price;
      return s + unit * i.qty;
    }, 0),
    [items, market]
  );
  const bundleSavings = Math.max(0, compareAtSubtotal - subtotal);
  const totalFreeTrackers = packBonusTrackers + (tier?.freeTrackers || 0);

  // 10% off (exit-intent) runs AFTER subtotal — mutually exclusive with the volume tier.
  const promoDiscount = promo === "SAVE10" && !tier ? Math.round(subtotal * 0.1) : 0;
  const totalWithPromo = Math.max(0, total - promoDiscount);

  // Fire-and-forget: capture the shopper's email + current cart on the server so the
  // 60-min cron sweeper can send a recovery email if they bail. Debounced so we don't
  // POST on every quantity tick.
  const saveEmail = async (addr) => {
    const clean = (addr || "").trim();
    if (!clean || !/@/.test(clean) || items.length === 0) return;
    try { localStorage.setItem("tracksy-email", clean); } catch {}
    setEmailSaved(true);
    // Pixel Advanced Matching — once we have an email, every downstream event
    // (InitiateCheckout, Purchase) carries hashed `em` for ad attribution.
    setAdvancedMatching({ email: clean });
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cart/capture`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: clean,
          items: items.map((i) => ({
            key: i.key,
            packId: i.packId,
            name: i.name,
            color: i.color,
            colorId: COLORS.find((c) => c.name === i.color)?.id || "obsidian",
            price: i.price,
            qty: i.qty,
          })),
          subtotal,
          total: totalWithPromo,
          platform,
        }),
      });
    } catch { /* non-fatal */ }
  };

  // Keep the server-side cart in sync whenever items change AFTER an email was captured.
  useEffect(() => {
    if (!emailSaved || !email || items.length === 0) return;
    const t = setTimeout(() => saveEmail(email), 1200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, subtotal, totalWithPromo, emailSaved]);

  // Cross-component promo bridge: the site-wide ExitIntentCapture fires this
  // custom event after a shopper submits their email on the exit modal. We
  // listen here so SAVE10 lands on the drawer's own total and the shopper sees
  // the discount applied the moment the drawer slides open.
  useEffect(() => {
    const onPromo = (e) => { if (e.detail === "SAVE10") setPromo("SAVE10"); };
    window.addEventListener("tracksy:apply-promo", onPromo);
    return () => window.removeEventListener("tracksy:apply-promo", onPromo);
  }, []);

  const tryClose = () => {
    let seen = false;
    try { seen = sessionStorage.getItem("tracksy-exit-shown") === "1"; } catch {}
    if (items.length > 0 && !promo && !seen) {
      try { sessionStorage.setItem("tracksy-exit-shown", "1"); } catch {}
      setExitOpen(true);
      return;
    }
    setIsOpen(false);
  };

  const applyExitPromo = () => {
    setPromo("SAVE10");
    setExitOpen(false);
    toast.success("10% off applied — code SAVE10");
  };

  const dismissExit = () => {
    setExitOpen(false);
    setIsOpen(false);
  };

  const checkout = async () => {
    setCheckingOut(true);
    // Stamp the checkout-attempt marker so CheckoutFeedback can detect
    // abandoned attempts on a later page view. Cleared on /payment/success.
    try { localStorage.setItem("tracksy:ck:lastAttempt", String(Date.now())); } catch {}
    // Clarity funnel step 2 — fires BEFORE the redirect so Clarity attaches it
    // to the current tracksyy.com session (the Shopify checkout tab is a new
    // session unless the Clarity Shopify-app embed stitches them).
    try {
      window.clarity?.("event", "checkout_clicked");
      window.clarity?.("set", "checkout_total", String(Math.round(totalWithPromo || 0)));
    } catch (e) { /* noop */ }
    try {
      const attribution = getAttribution();
      const webview = detectInAppBrowser();
      // AbortController kills the fetch at 12s so a stalled backend never
      // leaves the full-screen overlay spinning forever. The user gets a
      // clear toast + the overlay dismisses so they can retry.
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 12000);
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/payments/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        signal: controller.signal,
        body: JSON.stringify({
          items: items.map((i) => ({
            packId: i.packId,
            colorId: COLORS.find((c) => c.name === i.color)?.id || "obsidian",
            qty: i.qty,
          })),
          origin_url: window.location.origin,
          platform,
          promo_code: promo || undefined,
          email: email || undefined,
          currency: (currency || "AUD").toLowerCase(),
          travel_cards: travelCards || 0,
          fbp: attribution.fbp || undefined,
          fbc: attribution.fbc || undefined,
          user_agent: attribution.user_agent || undefined,
          webview: webview?.source || undefined,
          webview_platform: webview?.platform || undefined,
        }),
      });
      clearTimeout(timeoutId);
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Checkout failed");
      // Meta Pixel: InitiateCheckout — eventID mirrors the server-side
      // CAPI InitiateCheckout the backend fires on draft create so Meta can
      // dedupe them into one event.
      try {
        if (typeof window.fbq === "function") {
          window.fbq("track", "InitiateCheckout", {
            value: totalWithPromo || 0,
            currency: (currency || "AUD").toUpperCase(),
            content_type: "product",
            contents: items.map((i) => ({
              id: `${i.packId}-${COLORS.find((c) => c.name === i.color)?.id || "obsidian"}`,
              quantity: i.qty,
              item_price: i.unit,
            })),
            num_items: trackerCount || 0,
          }, { eventID: `ic_${data.session_id}` });
        }
      } catch { /* non-fatal */ }
      // In-app browser hand-off: FB / IG / TikTok Android WebView blocks
      // Shop Pay, Google Pay and PayPal from rendering; iOS WKWebView blocks
      // Apple Pay / Shop Pay the same way. If we're inside one of those,
      // route the shopper straight to the Shopify checkout URL in real
      // Chrome / Safari where one-tap payments actually work. Falls back to
      // normal in-page navigation on all first-party browsers.
      try {
        window.clarity?.("event", "checkout_handoff_attempt");
      } catch { /* noop */ }
      const handedOff = openUrlInSystemBrowser(data.checkout_url);
      if (handedOff) {
        try {
          window.clarity?.("event", "checkout_handoff_fired");
          window.clarity?.("set", "checkout_handoff", "1");
        } catch { /* noop */ }
        // Hand-off path has its OWN 800 ms safety timer inside
        // `openUrlInSystemBrowser` that falls back to in-WebView navigation
        // if the OS doesn't honour the scheme. Either way the shopper lands
        // on Shopify checkout within a second — never stuck on "Redirecting…".
        return;
      }
      window.location.href = data.checkout_url;
    } catch (e) {
      const aborted = e?.name === "AbortError";
      toast.error(
        aborted ? "Checkout is taking too long" : "Couldn't start checkout",
        { description: aborted ? "Please check your connection and try again." : (e.message || "Try again") }
      );
      // Fire a global event so CheckoutFeedback surfaces its card right away
      // with the exact error reason attached to the admin inbox entry.
      try {
        window.dispatchEvent(new CustomEvent("tracksy:checkout-error", {
          detail: { reason: aborted ? "timeout_12s" : ((e && e.message) ? String(e.message).slice(0, 200) : "unknown") },
        }));
      } catch { /* noop */ }
      setCheckingOut(false);
    }
  };

  return (
    <>
      <CheckoutRedirectOverlay
        visible={checkingOut}
        totalLabel={`${symbol}${totalWithPromo.toFixed(0)} ${(currency || "AUD").toUpperCase()}`}
      />
      <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            data-testid="cart-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={tryClose}
            className="fixed inset-0 z-[60] bg-black/40 backdrop-blur-sm"
          />
          <motion.aside
            data-testid="cart-drawer"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", stiffness: 300, damping: 32 }}
            className="fixed inset-y-0 right-0 z-[70] flex w-full max-w-md flex-col overflow-hidden bg-white shadow-2xl pb-[env(safe-area-inset-bottom)]"
          >
            {/* Header */}
            <div className="flex items-center justify-between border-b border-black/5 px-4 py-5 sm:px-6">
              <h3 className="flex items-center gap-2 font-heading text-lg font-extrabold text-tracksy-ink">
                <ShoppingBag className="h-5 w-5 text-tracksy-orange" />
                Your cart
                {items.length > 0 && reservedLabel && (
                  <span
                    data-testid="cart-reservation-timer"
                    className="ml-1 inline-flex items-center gap-1 rounded-full bg-tracksy-orange/10 px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wider text-tracksy-orange"
                    title="Items held at this price for 10 minutes"
                  >
                    <Timer className="h-3 w-3" /> {reservedLabel}
                  </span>
                )}
              </h3>
              <button
                data-testid="cart-close-button"
                onClick={tryClose}
                className="rounded-full p-2 text-tracksy-sub transition-colors hover:bg-tracksy-cream hover:text-tracksy-ink"
                aria-label="Close cart"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Country / currency clarity chip */}
            <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 border-b border-black/5 bg-tracksy-cream/50 px-4 py-2 text-[11px] font-bold text-tracksy-ink sm:px-6" data-testid="cart-country-chip">
              <button
                type="button"
                onClick={() => {
                  const cycle = ["AU", "US", "GB"];
                  const i = cycle.indexOf(market.code);
                  setCode(cycle[(i + 1) % cycle.length] || "AU");
                }}
                className="flex items-center gap-1.5 transition-colors hover:text-tracksy-orange"
                data-testid="cart-country-toggle"
                aria-label="Switch shipping country"
              >
                <span className="text-sm leading-none">{market.flag}</span>
                Shipping to {market.name} · {currency}
                <ChevronRight className="h-3 w-3 opacity-50" />
              </button>
              <span className="flex items-center gap-1 text-tracksy-green">
                <ShieldCheck className="h-3 w-3" /> Insured in transit
              </span>
            </div>

            {/* Free-shipping progress */}
            <div className="border-b border-black/5 px-4 py-4 sm:px-6">
              <div className="flex items-center gap-2 text-xs font-semibold text-tracksy-sub">
                <Truck className="h-4 w-4 text-tracksy-orange" />
                {remaining > 0 ? (
                  <span data-testid="shipping-progress-label">
                    ${remaining.toFixed(0)} away from <span className="text-tracksy-ink">free express shipping</span>
                  </span>
                ) : (
                  <span data-testid="shipping-progress-label" className="text-tracksy-green">
                    Free express shipping unlocked
                  </span>
                )}
              </div>
              <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-tracksy-cream">
                <motion.div
                  data-testid="shipping-progress-bar"
                  className={`h-full rounded-full ${remaining > 0 ? "bg-tracksy-orange" : "bg-tracksy-green"}`}
                  animate={{ width: `${progress}%` }}
                  transition={{ type: "spring", stiffness: 120, damping: 20 }}
                />
              </div>
              {items.length > 0 && (
                <div className="mt-3">
                  <RecentBuysTicker />
                </div>
              )}
            </div>

            {/* Items / empty state */}
            <div className="flex-1 overflow-y-auto px-4 py-4 sm:px-6">
              {items.length === 0 && travelCards === 0 ? (
                <div data-testid="cart-empty-state" className="flex h-full flex-col items-center justify-center gap-3 text-center">
                  <span className="flex h-16 w-16 items-center justify-center rounded-full bg-tracksy-cream">
                    <ShoppingBag className="h-7 w-7 text-tracksy-muted" />
                  </span>
                  <p className="font-heading text-base font-bold text-tracksy-ink">Your cart is empty</p>
                  <p className="text-sm text-tracksy-sub">Add a bundle to track your vehicles, equipment and essentials.</p>
                  <Link
                    to="/shop"
                    onClick={() => setIsOpen(false)}
                    className="mt-2 rounded-full bg-tracksy-ink px-6 py-2.5 text-sm font-bold text-white transition-colors hover:bg-tracksy-orange"
                  >
                    Shop Tracksy
                  </Link>
                </div>
              ) : (
                <ul className="space-y-4">
                  <AnimatePresence initial={false}>
                    {items.map((item) => {
                      const left = stockLeft(item.key);
                      const pack = PACKS.find((p) => p.id === item.packId);
                      const compareAtUnit = packCompareAt(pack, market);
                      const hasCompareAt = compareAtUnit && compareAtUnit > item.price;
                      const freeBonusPerUnit = pack?.freeBonus || 0;
                      const lineFreeBonus = freeBonusPerUnit * item.qty;
                      return (
                        <motion.li
                          key={item.key}
                          data-testid={`cart-item-${item.key}`}
                          layout
                          initial={{ opacity: 0, y: 12 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, x: 40 }}
                          className="flex items-center gap-4 rounded-2xl border border-black/5 bg-tracksy-paper p-4"
                        >
                          {item.image ? (
                            <img
                              src={item.image}
                              alt={`${item.name} in ${item.color}`}
                              className="h-14 w-14 shrink-0 rounded-xl border border-black/10 object-cover"
                            />
                          ) : (
                            <span
                              className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-black/10"
                              style={{ backgroundColor: item.colorHex }}
                            >
                              <span className="h-4 w-4 rounded-full bg-white/80" />
                            </span>
                          )}
                          <div className="flex-1">
                            <p className="text-sm font-bold text-tracksy-ink">{item.name}</p>
                            <p className="text-xs text-tracksy-sub">{item.color}</p>
                            {lineFreeBonus > 0 && (
                              <p
                                data-testid={`cart-free-bonus-${item.key}`}
                                className="mt-1 inline-flex items-center gap-1 rounded-full bg-tracksy-green/15 px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wider text-tracksy-green"
                              >
                                <Gift className="h-2.5 w-2.5" />
                                +{lineFreeBonus} FREE tracker{lineFreeBonus === 1 ? "" : "s"}
                              </p>
                            )}
                            {left <= 20 && (
                              <p className="mt-1 inline-flex items-center gap-1 rounded-full bg-tracksy-orange/10 px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wider text-tracksy-orange" data-testid={`cart-stock-${item.key}`}>
                                <span className="h-1.5 w-1.5 rounded-full bg-tracksy-orange" />
                                Only {left} left in stock
                              </p>
                            )}
                            <div className="mt-2 flex items-center gap-2">
                              <button
                                data-testid={`cart-qty-minus-${item.key}`}
                                onClick={() => updateQty(item.key, -1)}
                                className="flex h-8 w-8 items-center justify-center rounded-full border border-black/10 text-tracksy-sub hover:bg-white"
                                aria-label="Decrease"
                              >
                                <Minus className="h-3.5 w-3.5" />
                              </button>
                              <span className="w-6 text-center text-sm font-bold">{item.qty}</span>
                              <button
                                data-testid={`cart-qty-plus-${item.key}`}
                                onClick={() => updateQty(item.key, 1)}
                                className="flex h-8 w-8 items-center justify-center rounded-full border border-black/10 text-tracksy-sub hover:bg-white"
                                aria-label="Increase"
                              >
                                <Plus className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-extrabold text-tracksy-ink">
                              ${(item.price * item.qty).toFixed(0)}
                            </p>
                            {hasCompareAt && (
                              <p
                                data-testid={`cart-compare-at-${item.key}`}
                                className="text-[11px] font-semibold text-tracksy-sub line-through decoration-tracksy-sub/70"
                              >
                                {symbol}{(compareAtUnit * item.qty).toFixed(0)}
                              </p>
                            )}
                            {pack?.discountPct && hasCompareAt && (
                              <span
                                data-testid={`cart-discount-pct-${item.key}`}
                                className="mt-1 inline-flex items-center rounded-md bg-tracksy-orange px-1.5 py-0.5 text-[9px] font-black uppercase leading-none tracking-wide text-white"
                              >
                                −{pack.discountPct}%
                              </span>
                            )}
                            <button
                              data-testid={`cart-remove-${item.key}`}
                              onClick={() => removeItem(item.key)}
                              className="mt-2 text-tracksy-muted transition-colors hover:text-red-500"
                              aria-label="Remove item"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </motion.li>
                      );
                    })}
                  </AnimatePresence>

                  {/* Travel Card row — matches the standard pack row styling
                      so it looks like a normal product in the cart (no special
                      bundle framing). Qty / remove behave identically. */}
                  {travelCards > 0 && (
                    <motion.li
                      data-testid="cart-travelcard-row"
                      layout
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, x: 40 }}
                      className="flex items-center gap-4 rounded-2xl border border-black/5 bg-tracksy-paper p-4"
                    >
                      <div className="relative h-14 w-14 shrink-0 overflow-hidden rounded-xl border border-black/10 bg-tracksy-cream">
                        <img
                          src="/images/travel-card.webp"
                          alt="Tracksy Travel Card"
                          className="h-full w-full object-contain p-0.5"
                          style={{ mixBlendMode: "multiply" }}
                        />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-bold text-tracksy-ink">Tracksy Travel Card</p>
                        <p className="text-xs text-tracksy-sub">Dual-ecosystem · Apple + Google</p>
                        <div className="mt-2 flex items-center gap-2">
                          <button
                            data-testid="cart-travelcard-minus"
                            onClick={() => updateTravelCards(-1)}
                            className="flex h-8 w-8 items-center justify-center rounded-full border border-black/10 text-tracksy-sub hover:bg-white"
                            aria-label="Decrease"
                          >
                            <Minus className="h-3.5 w-3.5" />
                          </button>
                          <span className="w-6 text-center text-sm font-bold">{travelCards}</span>
                          <button
                            data-testid="cart-travelcard-plus"
                            onClick={() => updateTravelCards(1)}
                            className="flex h-8 w-8 items-center justify-center rounded-full border border-black/10 text-tracksy-sub hover:bg-white"
                            aria-label="Increase"
                          >
                            <Plus className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-extrabold text-tracksy-ink">
                          {symbol}{(travelCardUnit * travelCards).toFixed(0)}
                        </p>
                        <button
                          data-testid="cart-travelcard-remove"
                          onClick={removeTravelCards}
                          className="mt-2 text-tracksy-muted transition-colors hover:text-red-500"
                          aria-label="Remove Travel Cards"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </motion.li>
                  )}
                  {/* Free Travel Card bonus — same row footprint as a paid product
                      so it reads as a real item in the cart, but wrapped in a
                      bold green/gradient treatment with the actual product photo
                      and a FREE pill. Removes any doubt about "is this included?". */}
                  {freeTravelCards > 0 && (
                    <motion.li
                      data-testid="cart-travelcard-free-row"
                      layout
                      initial={{ opacity: 0, y: 12, scale: 0.98 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      transition={{ type: "spring", stiffness: 220, damping: 24 }}
                      className="relative flex items-center gap-4 overflow-hidden rounded-2xl border-2 border-tracksy-green/50 bg-gradient-to-br from-tracksy-green/15 via-white to-tracksy-green/5 p-4 shadow-sm"
                    >
                      {/* Corner FREE ribbon */}
                      <span className="absolute -right-9 top-3 rotate-45 bg-tracksy-green px-10 py-0.5 text-[9px] font-black uppercase tracking-widest text-white shadow-md">
                        Free
                      </span>
                      <div className="relative h-14 w-14 shrink-0 overflow-hidden rounded-xl border border-black/5 bg-tracksy-cream">
                        <img
                          src="/images/travel-card.webp"
                          alt="Tracksy Travel Card — included free with your bundle"
                          className="h-full w-full object-contain p-0.5"
                          style={{ mixBlendMode: "multiply" }}
                        />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-black text-tracksy-ink">
                          Tracksy Travel Card
                          <span className="ml-1.5 text-tracksy-sub">×{freeTravelCards}</span>
                        </p>
                        <p className="text-[11px] font-semibold text-tracksy-sub">
                          Dual-ecosystem · Apple + Google · 2.0mm
                        </p>
                        <p className="mt-1.5 inline-flex items-center gap-1 rounded-full bg-tracksy-green px-2 py-0.5 text-[10px] font-black uppercase tracking-wider text-white shadow-sm">
                          <Gift className="h-2.5 w-2.5" />
                          Free bundle bonus · ships with order
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-[11px] font-semibold text-tracksy-sub line-through decoration-tracksy-sub/70">
                          {symbol}{(travelCardUnit * freeTravelCards).toFixed(0)}
                        </p>
                        <p
                          data-testid="cart-travelcard-free-amount"
                          className="font-heading text-base font-black text-tracksy-green"
                        >
                          FREE
                        </p>
                      </div>
                    </motion.li>
                  )}
                </ul>
              )}

              {/* Merchandising blocks live INSIDE the scrollable area so the
                  pinned footer below never pushes the checkout button offscreen on mobile. */}
              {items.length > 0 && (
                <div className="mt-5 space-y-4">
                  <Upsell />
                  {tier && (
                    <div data-testid="cart-tier-perks" className="rounded-2xl border border-tracksy-orange/30 bg-white p-4">
                      <p className="text-xs font-bold uppercase tracking-widest text-tracksy-orange">
                        {tier.name} tier unlocked · {tier.headline}
                      </p>
                      <ul className="mt-2 space-y-1">
                        {tier.perks.map((p) => (
                          <li key={p} className="flex gap-2 text-xs text-tracksy-ink">
                            <Gift className="mt-0.5 h-3.5 w-3.5 shrink-0 text-tracksy-orange" />{p}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <NextTierNudge count={trackerCount} />
                  {totalFreeTrackers > 0 && (
                    <div data-testid="cart-free-trackers" className="relative overflow-hidden rounded-2xl border-2 border-tracksy-green/40 bg-gradient-to-br from-tracksy-green/15 via-tracksy-green/5 to-white p-4">
                      <div className="flex items-start gap-3">
                        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-tracksy-green text-white shadow-sm">
                          <Gift className="h-5 w-5" />
                        </span>
                        <div className="flex-1">
                          <p className="text-[11px] font-extrabold uppercase tracking-[0.18em] text-tracksy-green">
                            {packBonusTrackers > 0 && !tier ? "Limited-time bonus" : `${tier?.name || "Bundle"} reward`}
                          </p>
                          <p className="mt-0.5 font-heading text-lg font-extrabold leading-tight text-tracksy-ink">
                            +{totalFreeTrackers} free tracker{totalFreeTrackers === 1 ? "" : "s"} added
                          </p>
                          <p className="mt-0.5 text-xs text-tracksy-sub">
                            You're paying for <strong className="text-tracksy-ink">{trackerCount}</strong> — we're shipping <strong className="text-tracksy-ink">{trackerCount + totalFreeTrackers}</strong>.
                          </p>
                        </div>
                        <span className="shrink-0 rounded-full bg-tracksy-green px-2.5 py-1 text-[11px] font-extrabold text-white">
                          Worth ${totalFreeTrackers * (market?.currency === "USD" ? 21 : 29)}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Delivery date — single biggest urgency driver */}
                  <div className="flex items-center gap-3 rounded-2xl border border-tracksy-green/25 bg-tracksy-green/5 p-3" data-testid="cart-delivery-estimate">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-tracksy-green/15 text-tracksy-green">
                      <PackageCheck className="h-5 w-5" />
                    </span>
                    <div className="flex-1 text-xs leading-tight">
                      {delivery.shipsToday ? (
                        <>
                          <p className="font-extrabold text-tracksy-ink">
                            Order in the next <span className="text-tracksy-green">{delivery.countdown}</span> — arrives by {delivery.arrivesBy}
                          </p>
                          <p className="mt-0.5 text-tracksy-sub">Dispatched today · {market.shippingCopy}.</p>
                        </>
                      ) : (
                        <>
                          <p className="font-extrabold text-tracksy-ink">
                            Dispatched tomorrow — arrives by {delivery.arrivesBy}
                          </p>
                          <p className="mt-0.5 text-tracksy-sub">{market.shippingCopy}.</p>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Desktop-only secondary trust blocks — hidden on mobile where the pinned CTA is king */}
                  <div className="hidden sm:block space-y-3">
                    <div className="grid grid-cols-4 gap-2 rounded-xl bg-tracksy-cream/70 px-2 py-2 text-center text-[10px] font-bold text-tracksy-ink" data-testid="cart-trust-ribbon">
                      <div className="flex flex-col items-center gap-0.5">
                        <Lock className="h-3.5 w-3.5 text-tracksy-ink" />
                        <span>Shop Pay</span>
                      </div>
                      <div className="flex flex-col items-center gap-0.5">
                        <Truck className="h-3.5 w-3.5 text-tracksy-orange" />
                        <span>Free AU shipping</span>
                      </div>
                      <div className="flex flex-col items-center gap-0.5">
                        <RefreshCcw className="h-3.5 w-3.5 text-tracksy-green" />
                        <span>30-day returns</span>
                      </div>
                      <div className="flex flex-col items-center gap-0.5 text-tracksy-ink">
                        <Star className="h-3.5 w-3.5 fill-tracksy-orange text-tracksy-orange" />
                        <span>4.8 · 2,143</span>
                      </div>
                    </div>
                    <PaymentLogos />
                    <p className="flex items-center justify-center gap-1.5 text-center text-[11px] font-semibold text-tracksy-ink" data-testid="cart-reship-promise">
                      <RefreshCcw className="h-3 w-3 text-tracksy-green" />
                      Any issue? We'll reship free. No forms, no fuss.
                    </p>
                    <div className="grid grid-cols-3 gap-2 border-t border-black/5 pt-3 text-center" data-testid="cart-next-steps">
                      <div>
                        <div className="mx-auto flex h-6 w-6 items-center justify-center rounded-full bg-tracksy-cream text-[10px] font-extrabold text-tracksy-ink">1</div>
                        <p className="mt-1.5 text-[10px] font-bold leading-tight text-tracksy-ink">Pay securely</p>
                        <p className="mt-0.5 text-[9px] leading-tight text-tracksy-sub">Shop Pay · 1-tap</p>
                      </div>
                      <div>
                        <div className="mx-auto flex h-6 w-6 items-center justify-center rounded-full bg-tracksy-cream text-[10px] font-extrabold text-tracksy-ink">2</div>
                        <p className="mt-1.5 text-[10px] font-bold leading-tight text-tracksy-ink">We dispatch</p>
                        <p className="mt-0.5 text-[9px] leading-tight text-tracksy-sub">{delivery.shipsToday ? "Today" : "Tomorrow"}</p>
                      </div>
                      <div>
                        <div className="mx-auto flex h-6 w-6 items-center justify-center rounded-full bg-tracksy-cream text-[10px] font-extrabold text-tracksy-ink">3</div>
                        <p className="mt-1.5 text-[10px] font-bold leading-tight text-tracksy-ink">Arrives</p>
                        <p className="mt-0.5 text-[9px] leading-tight text-tracksy-sub">By {delivery.arrivesBy}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Pinned footer: Total + CTA only — this is ALWAYS visible on every screen size,
                including mobile, so the checkout button is one tap away no matter how long the cart. */}
            {items.length > 0 && (
              <div className="border-t border-black/5 bg-white px-4 pt-3 pb-3 sm:px-6 sm:pt-4 sm:pb-5 shadow-[0_-8px_24px_-16px_rgba(0,0,0,0.12)]">
                {/* Hero shipped-count ribbon — shows when any free trackers (pack bonus or tier) are in play */}
                {totalFreeTrackers > 0 && (
                  <div className="mb-3 flex items-center justify-between gap-3 rounded-xl bg-tracksy-green px-3 py-2 text-white" data-testid="cart-shipped-ribbon">
                    <span className="flex items-center gap-2 text-xs font-extrabold uppercase tracking-wider">
                      <Gift className="h-4 w-4" />
                      Shipping {trackerCount + totalFreeTrackers} trackers
                    </span>
                    <span className="text-[11px] font-bold opacity-90">
                      {trackerCount} paid + {totalFreeTrackers} free
                    </span>
                  </div>
                )}

                {/* Totals */}
                <div className="mb-1 flex items-baseline justify-between text-xs">
                  <span className="font-semibold text-tracksy-sub">
                    Subtotal · {trackerCount} paid tracker{trackerCount === 1 ? "" : "s"}
                  </span>
                  <span className="flex items-baseline gap-1.5">
                    {bundleSavings > 0 && (
                      <span data-testid="cart-compare-subtotal" className="text-[11px] font-semibold text-tracksy-sub line-through decoration-tracksy-sub/70">
                        {symbol}{compareAtSubtotal.toFixed(0)}
                      </span>
                    )}
                    <span data-testid="cart-subtotal" className="font-semibold text-tracksy-ink">{symbol}{subtotal.toFixed(0)}</span>
                  </span>
                </div>
                {bundleSavings > 0 && (
                  <div className="mb-1 flex items-baseline justify-between text-xs" data-testid="cart-bundle-savings-row">
                    <span className="font-semibold text-tracksy-green">Bundle savings</span>
                    <span className="font-semibold text-tracksy-green">−${bundleSavings.toFixed(0)}</span>
                  </div>
                )}
                {totalFreeTrackers > 0 && (
                  <div className="mb-1 flex items-baseline justify-between text-xs" data-testid="cart-free-trackers-row">
                    <span className="font-semibold text-tracksy-green">
                      + {totalFreeTrackers} free tracker{totalFreeTrackers === 1 ? "" : "s"}
                    </span>
                    <span className="font-semibold text-tracksy-green">INCLUDED</span>
                  </div>
                )}
                {discount > 0 && (
                  <div className="mb-1 flex items-center justify-between text-xs">
                    <span className="font-semibold text-tracksy-green">Volume ({Math.round(tier.discount * 100)}%)</span>
                    <span data-testid="cart-discount" className="font-semibold text-tracksy-green">−${discount}</span>
                  </div>
                )}
                {promoDiscount > 0 && (
                  <div className="mb-1 flex items-center justify-between text-xs" data-testid="cart-promo-row">
                    <span className="font-semibold text-tracksy-green">SAVE10 (10% off)</span>
                    <span className="font-semibold text-tracksy-green">−${promoDiscount}</span>
                  </div>
                )}
                <div className="mb-3 flex items-center justify-between">
                  <span className="text-sm font-semibold text-tracksy-sub">Total</span>
                  <span data-testid="cart-total" className="font-heading text-xl font-extrabold text-tracksy-ink">${totalWithPromo.toFixed(0)}</span>
                </div>

                {/* Checkout CTA — the one button that always matters */}
                <button
                  data-testid="checkout-button"
                  disabled={checkingOut}
                  onClick={checkout}
                  className="flex w-full items-center justify-center gap-2 rounded-full bg-tracksy-orange py-4 text-base font-extrabold text-white shadow-glow transition-colors hover:bg-tracksy-orangeHover disabled:opacity-70"
                >
                  <Lock className="h-4 w-4" />
                  {checkingOut ? "Redirecting…" : `Checkout — ${currency === "USD" ? "US$" : "$"}${totalWithPromo.toFixed(0)}`}
                </button>

                {/* Minimal mobile trust row — one line, no icons collisions */}
                <p className="mt-2 flex items-center justify-center gap-2 text-center text-[10px] font-semibold text-tracksy-sub sm:hidden">
                  <Lock className="h-2.5 w-2.5" />
                  Shop Pay secure
                  <span className="text-tracksy-muted">·</span>
                  <Truck className="h-2.5 w-2.5 text-tracksy-orange" />
                  {market.shippingShort}
                  <span className="text-tracksy-muted">·</span>
                  <ShieldCheck className="h-2.5 w-2.5 text-tracksy-green" />
                  30-day returns
                </p>
              </div>
            )}

            {/* Exit-intent save overlay — rendered INSIDE the drawer, dismisses or applies SAVE10 */}
            <ExitIntentSave open={exitOpen} onApply={applyExitPromo} onDismiss={dismissExit} />
          </motion.aside>
        </>
      )}
    </AnimatePresence>
    </>
  );
};

export default CartDrawer;

export { Package }; // keep Package re-export if other modules relied on it
